import React, {ReactNode} from "react";
import {Button} from "../../basic-ui-components/button/base/button.component";
import {CyClass} from "../../../common/cy-classes";
import {OrbitComponent} from "../base-components/orbit-component/orbit-component";
import {LiveStreamingTileBox} from "./boxes/live-streaming-tile/live-streaming-tile.box";
import {LiveStreamingTileFooterBox} from "./boxes/live-streaming-tile-footer/live-streaming-tile-footer.box";
import {LiveStreamingTileGameTitleBox} from "./boxes/live-streaming-tile-game-title/live-streaming-tile-game-title.box";
import {LiveStreamingTileBettingRangeBox} from "./boxes/live-streaming-tile-betting-range/live-streaming-tile-betting-range.box";
import {LiveStreamingTileFooterSectionBox} from "./boxes/live-streaming-tile-footer/live-streaming-tile-footer-section.box";
import {AspectRatioContainerComponent} from "../raw-components/aspect-ratio-container/aspect-ratio-container.component";
import {LiveStreamingTileOverlayComponent} from "./live-streaming-tile-video-overlay.component";
import {LiveStreamingTileOverlayBox} from "./boxes/live-streaming-tile-overlay/live-streaming-tile-overlay.box";
import {LiveStreamingTileSkeletonContainerBox} from "./boxes/live-streaming-tile-skeleton-container/live-streaming-tile-skeleton-container.box";
import {LiveStreamingVideoComponent} from "./live-streaming-video.component";
import {action, observable} from "mobx";
import {observer} from "mobx-react";
import {RetryScreenIconBox} from "./boxes/live-streaming-tile-retry-screen/retry-screen-icon.box";
import {RetryScreenTextBox} from "./boxes/live-streaming-tile-retry-screen/retry-screen-text.box";
import {RetryScreenTitleBox} from "./boxes/live-streaming-tile-retry-screen/retry-screen-title.box";
import {SkeletonAnimatedButton} from "./boxes/live-streaming-tile-skeleton/skeleton-animated-button.box";
import {LiveStreamingTileRetryScreenBox} from "./boxes/live-streaming-tile-retry-screen/live-streaming-tile-retry-screen.box";
import {SvgSizeUnit} from "../../basic-ui-components/icon/svg-icon/svg.icon";
import {GameInfo} from "../../services/games/game-info";
import {ILiveTableInfo} from "../../services/live-table-feeds-service/live-table-feeds.service.interface";
import {Check} from "@sparkware/app-utils";
import { SingleGameSkeletonComponent } from "../single-game/single-game-skeleton.component";
import { LIVE_STREAMING_ITEM } from "../../themes/css-global-classes";
import { GA_LIVE_STREAM_CATEGORY } from "../../services/video-live-stream/video-live-stream.service";

export interface LiveStreamingTileComponentProps {
    gameInfo: GameInfo;
    onOpenRealGame: () => void;
}

@observer
export class LiveStreamingTileComponent extends OrbitComponent<LiveStreamingTileComponentProps> {

    @observable
    private _hasError: boolean = false;
    @action.bound
    setHasError(value: boolean) {
        this._hasError = value;
    }
    @observable
    private _isVideoLoading: boolean = true;
    @observable
    private _isFirstTimeLoading: boolean = true;
    @action.bound
    setIsVideoLoading(value: boolean) {
        this._isVideoLoading = value;
        if (this._isFirstTimeLoading === true && value === false) {
            this._isFirstTimeLoading = false;
        }
    }
    @observable
    private _isWatchTimeLimitReached: boolean = false;
    @action.bound
    setIsWatchTimeLimitReached(value: boolean) {
        this._isWatchTimeLimitReached = value;
    }

    private _onPlayClickHandler = () => {
        this.props.onOpenRealGame();
        this.services.analytics.sendAnalytics(GA_LIVE_STREAM_CATEGORY, "PLAY_GAME", "CLICK", this.props.gameInfo.gameType, true);
    }

    private _renderPlayerOverlay() {
        if (!this._isWatchTimeLimitReached && !this._isVideoLoading) {
            return null;
        }

        let statusText = '';
        if (this._isWatchTimeLimitReached) {
            statusText = this.services.translation.translate('LIVE_STREAM_WATCH_TIME_REACHED');
        } else if (this._isVideoLoading) {
            statusText = this.services.translation.translate('LIVE_STREAM_VIDEO_LOADING');
        }

        return <LiveStreamingTileOverlayBox>{statusText}</LiveStreamingTileOverlayBox>;
    }

    private _renderRetryScreen() {

        const BrokenLinkIcon = this.services.theme.currentTheme.icons.brokenLinkIcon;

        return (
            <React.Fragment>
                <AspectRatioContainerComponent width={16} height={9}>
                    <LiveStreamingTileRetryScreenBox>
                        <RetryScreenIconBox>
                            <BrokenLinkIcon size={4} sizeUnit={SvgSizeUnit.Rem} />
                        </RetryScreenIconBox>
                        <RetryScreenTitleBox>
                            {this.services.translation.translate('CANT_CONNECT_ERROR_TITLE')}
                        </RetryScreenTitleBox>
                        <RetryScreenTextBox>
                            {this.services.translation.translate('CANT_CONNECT_ERROR_MESSAGE')}
                        </RetryScreenTextBox>
                        <Button preset={"primary"} sizeVariant={"small"} onClick={() => this.setHasError(false)}>
                            {this.services.translation.translate('RETRY')}
                        </Button>
                    </LiveStreamingTileRetryScreenBox>
                </AspectRatioContainerComponent>
                <LiveStreamingTileFooterBox>
                    <SkeletonAnimatedButton width={"115px"} height={"30px"} borderRadius={"15px"}/>
                </LiveStreamingTileFooterBox>
            </React.Fragment>
        );
    }

    private _renderPlayer(tableInfo: ILiveTableInfo) {
        return (
            <AspectRatioContainerComponent width={16} height={9}>
                <LiveStreamingVideoComponent
                    gameType={this.props.gameInfo.gameType}
                    muted={this.services.httpLiveStream.isVideoMuted}
                    onError={value => this.setHasError(value)}
                    onLoading={(value) => {this.setIsVideoLoading(value)}}
                    onWatchTimeLimitReached={(value) => {this.setIsWatchTimeLimitReached(value)}}
                />
                {this._renderPlayerOverlay()}
                <LiveStreamingTileOverlayComponent tableInfo={tableInfo}/>
            </AspectRatioContainerComponent>
        );
    }

    private _renderMinMaxBet(tableInfo: ILiveTableInfo): ReactNode {

        let minBet: number;
        let maxBet: number;

        if(Check.isNullOrUndefined(tableInfo?.MinBet) || Check.isNullOrUndefined(tableInfo?.MaxBet)){
            minBet = this.props.gameInfo.minBet;
            maxBet = this.props.gameInfo.maxBet;
        } else {
            minBet = tableInfo.MinBet;
            maxBet = tableInfo.MaxBet;
        }

        return (
            <LiveStreamingTileBettingRangeBox>
                {this.services.currency.formatMinMaxBet(minBet, maxBet)}
            </LiveStreamingTileBettingRangeBox>
        );
    }

    private _renderGameTitle(tableInfo: ILiveTableInfo): ReactNode {

        let title = this.props.gameInfo?.title || "\xa0";

        if (tableInfo?.Name?.length > 0) {
            title = tableInfo.Name;
        }

        return (
            <LiveStreamingTileGameTitleBox>
                {title}
            </LiveStreamingTileGameTitleBox>
        );
    }

    private _renderFooter(tableInfo: ILiveTableInfo) {
        return (
            <LiveStreamingTileFooterBox className={CyClass("cy-orbit-swiper-streaming-tile-footer")}>
                <LiveStreamingTileFooterSectionBox>
                    {this._renderGameTitle(tableInfo)}
                    {this._renderMinMaxBet(tableInfo)}
                </LiveStreamingTileFooterSectionBox>
                <LiveStreamingTileFooterSectionBox>
                    <Button
                        margin="0 0 0 1rem"
                        preset={"secondary"}
                        sizeVariant={"small"}
                        onClick={this._onPlayClickHandler}
                    >
                        {this.services.translation.translate("PLAY_NOW")}
                    </Button>
                </LiveStreamingTileFooterSectionBox>
            </LiveStreamingTileFooterBox>
        );
    }

    private _renderSkeleton() {
        if (this._isFirstTimeLoading) {
            return (
                <LiveStreamingTileSkeletonContainerBox>
                    <SingleGameSkeletonComponent
                        templateType={LIVE_STREAMING_ITEM}
                        useSquareIcons={false}
                        placeholderImageSrc={''}
                    />
                </LiveStreamingTileSkeletonContainerBox>
            )
        }
    }

    private _renderContent() {
        if (this._hasError) {
            return this._renderRetryScreen();
        }

        const tableInfo = this.services.liveTableFeeds.getTableInfo(this.props.gameInfo.gameType);

        return (
            <React.Fragment>
                {this._renderPlayer(tableInfo)}
                {this._renderFooter(tableInfo)}
                {this._renderSkeleton()}
            </React.Fragment>
        );
    }

    render() {
        return (
            <LiveStreamingTileBox className={CyClass("cy-orbit-swiper-streaming-tile")}>
                {this._renderContent()}
            </LiveStreamingTileBox>
        );
    }
}
